import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {distinctUntilChanged, filter, first, map, switchMap, tap} from 'rxjs/operators';
import {Foo} from '../actions';
import * as fromStore from '../index';
import {Action, Store} from '@ngrx/store';
import {DbStoreState} from '../reducers';
import {ROUTER_NAVIGATION} from '@ngrx/router-store';
import {RouteDataService} from "../../shared/services/route-data/route-data.service";
import * as performanceAction from "../actions/performance.action";
import * as hostingInfoActions from '../actions/hosting-info.action';
import * as ecommerceMerchant from '../actions/ecommerce-create-merchant.action';
import * as ecommerceEmailsList from '../actions/ecommerce-emails-list.action';
import * as ecommerceTaxSettings from '../actions/ecommerce-tax-settings.action';
import * as ecommerceTaxClasses from '../actions/ecommerce-tax-classes.action';
import * as ecommerceTaxRates from '../actions/ecommerce-tax-rates.action';
import * as ecommerceGeneralSettings from '../actions/ecommerce-general-settings.action';
import * as ecommerceEmailsSettings from '../actions/ecommerce-emails-settings.action';
import * as EcommerceSetupListActions from '../actions/ecommerce-setup-list.action';
import * as ecommercePaymentMethods from '../actions/ecommerce-payment-methods.action';
import * as ecommercePublicKey from '../actions/ecommerce-public-key.action';
import * as ecommerceAnalytics from '../actions/ecommerce-analytics.action';
import * as ecommerceRefunds from '../actions/ecommerce-refunds.action';
import * as ecommercePerformance from '../actions/ecommerce-performance.action';
import * as ecommercePerformanceNow from '../actions/ecommerce-performance-now.action';
import * as ecommercePerformanceReportOrders from '../actions/ecommerce-performance-report.action';
import * as ecommercePerformanceReportOrdersLastYear from '../actions/ecommerce-performance-report-last-year.action';
import * as ecommercePerformanceReportNetSales from '../actions/ecommerce-performance-net-sales.action';
import * as ecommercePerformanceReportNetSalesLastYear
    from '../actions/ecommerce-performance-net-sales-last-year.action';
import * as hostingCertificatesAction from "../actions/hosting-certificates.action";
import * as hostingCacheExcludeAction from "../actions/hosting-cache-exclude.action";
import * as hostingSettingsAction from "../actions/hosting-settings.action";
import * as hostingPhpConfigsAction from "../actions/hosting-php-configs.action";
import * as hostingDomainsActions from "../actions/hosting-domains.action";
import * as websiteProductActions from "../actions/website-product.action";
import * as websiteWpVersionActions from "../actions/website-wp-version.action";
import * as hostingRedirectsActions from "../actions/hosting-redirects.action";
import * as securityAction from "../actions/security.action";
import * as hostingLogsActions from "../actions/hosting-logs.action";
import * as HostingResourcesAction from "../actions/hosting-resources.action";
import * as HostingVisitsAction from "../actions/hosting-visits.action";
import * as workspaceActions from '../actions/workspace.action';
import * as aiBuilderActions from '../actions/ai-builder.action';
import * as boosterActions from '../actions/booster.action';
import * as cloudflareDataActions from '../actions/cloudflare-data.action';
import * as cacheRulesActions from '../actions/cache-rules.action';
import * as bandwidthActions from '../actions/bandwidth.action';
import * as boosterDataActions from '../actions/booster-data.action';
import * as boosterAdvancedSettings from '../actions/booster-advanced-settings.action';
import * as imageOptimizerActions from '../actions/image-optimizer.action';
import * as troubleShootDataActions from '../actions/troubleshoot.action';
import * as expandedDomainDataActions from '../actions/domain-expanded-data.action';
import * as tenWebApiActions from '../actions/10web-api.actions';
import {RouteData} from "../../shared/services/route-data/route-data";
import {Product} from "../../websites/website/product";
import * as builderTemplatesAction from '../actions/builder-templates.action';
import * as websiteTagsAction from '../actions/website-tags.action';
import * as userPaymentMethodsAction from '../actions/user-payment-methods.action';
import * as userPaymentsAction from '../actions/payments.action';
import * as vibeLimitationsActions from '../actions/vibe-limitations.action';
import {User} from "../../user/user";
import {RouterNavigationAction} from "@ngrx/router-store/src/actions";
import {TypeUtils} from "../../shared/utils/type-utils";
import {Utils} from "../../shared/utils/utils";
import {Config} from "../../config/config";
import * as fromUserSelectors from "../selectors/user.selector";
import {combineLatest} from "rxjs";
import {clientRoles} from "../../user/user-roles-actions";

@Injectable()
export class RouterEffects {

    private _resourceMap: Object;
    private _loadedResources: string[];
    private _childWebsiteId: any;
    private _website: any;
    private _websiteUrl: any;
    private _user: User;
    protected readonly clientRoles = clientRoles;

    constructor(
        private _store: Store<DbStoreState>,
        private _actions$: Actions,
        private _routeDataService: RouteDataService,
    ) {
        this._resourceMap = {};
        this._loadedResources = [];
    }


    routerNavigatedPerformanceHistory$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'performance-history'),
        switchMap((response: any) => {
            const actions = ['performancesHistory'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigatedPerformanceTest$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'performance-test'),
        switchMap((response: any) => {
            const actions = ['performancesTest'];
            const otherData = response.url.split('/test/')[1] ? response.url.split('/test/')[1] : null;
            return this.getActions(actions, response.queryParams.id, otherData);
        })
    ));

    routerNavigatedOverview$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION), // use the pipeable ofType operator
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'main'),
        switchMap((routeData) => combineLatest([
            this._store.select(fromStore.getUserSubscription),
            this._store.select(fromStore.getWebsitesLoaded)
        ]).pipe(
            filter(([userSubscription, websitesLoaded]) => !!userSubscription && !!websitesLoaded),
            switchMap(([userSubscription]) => this._store.select(fromStore.getWebsite(routeData.queryParams.id)).pipe(
                filter((response: any) => {
                    if (response && response.isNetwork && !response.parentId && response.children && response.children.length) {
                        this._childWebsiteId = response.children[0];
                    } else {
                        this._childWebsiteId = null;
                    }
                    return !!(response);
                }),
                map(() => ({routeData, userSubscription}))
            ))
        )),
        switchMap(({routeData, userSubscription}: any) => {
            const actions = ["loadWebsiteProducts", "loadWebsiteWPVersion", 'loadHostingSettings', 'loadCertificates'];
            userSubscription?.plan?.vibeService && actions.push('loadVibeLimitations');
            return this.getActions(actions, this._childWebsiteId || routeData.queryParams.id);
        })));

    routerNavigatedHostingDomains$ = createEffect(() => this._actions$.pipe(
        ofType<RouterNavigationAction>(ROUTER_NAVIGATION),
        map(event => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter(routeData => routeData.data.pageId === 'domains'),
        switchMap(routeData => {
            return this._store.select(fromStore.getWebsitesLoaded)
                .pipe(
                    filter(loaded => loaded),
                    switchMap(() => this._store.select(fromStore.getWebsite(routeData.queryParams.id))),
                    tap(_response => {
                        this._childWebsiteId = _response && _response.isNetwork && !_response.parentId
                        && _response.children && _response.children.length ? _response.children[0] : null;
                    }),
                    map(() => routeData.queryParams.id)
                );
        }),
        switchMap(websiteId => {
            const actions = ['loadDomains', "loadZones", "loadResellerMainDomain"];
            return this.getActions(actions, websiteId);
        })
    ));

    routerDomainExpandedDataTools$ = createEffect(() => this._actions$.pipe(
        ofType<RouterNavigationAction>(ROUTER_NAVIGATION),
        map(event => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter(routeData => routeData.data.pageId === 'domains'),
        switchMap(routeData => {
            // Check if websites are loaded and fetch the website based on the route's queryParams.id
            return this._store.select(fromStore.getWebsitesLoaded)
                .pipe(
                    filter(loaded => loaded),
                    switchMap(() => this._store.select(fromStore.getWebsite(routeData.queryParams.id))),
                    tap(_response => {
                        this._childWebsiteId = _response && _response.isNetwork && !_response.parentId
                        && _response.children && _response.children.length ? _response.children[0] : null;
                    }),
                    switchMap(_response => {
                        const useChildIdForDomains = _response && _response.multisiteType && _response.multisiteType === 'subfolder' && _response.children && _response.children.length;
                        const idForDomainFetch = useChildIdForDomains ? _response.children[0] : routeData.queryParams.id;
                        return this._store.select(fromStore.getDomainsLoaded(idForDomainFetch))
                            .pipe(
                                filter(loaded => loaded),
                                switchMap(() => this._store.select(fromStore.getDomains(idForDomainFetch))),
                                filter((response: any) => !!response),
                                map(response => ({response, websiteId: idForDomainFetch}))
                            );
                    })
                );
        }),
        switchMap(({response, websiteId}) => {
            if (response && response.length !== 1) {
                // Filter domains based on a blacklist and map to their IDs
                const domainIds = response
                    .filter(domain => !Config.hostnameList.some(hostname => domain.name.endsWith(hostname)))
                    .map(domain => domain.id);

                // Execute actions to load expanded domain data
                const actions = ['loadExpandedDomainData'];
                return this.getActions(actions, websiteId, {domainIds: domainIds, expanded: true});
            } else {
                return this.getActions(['empty']);
            }
        })
    ));


    routerNavigatedHostingInfo$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'hosting-info' || routeData.data.pageId === 'manage-environment' || routeData.data.pageId === 'onboarding'),
        switchMap((response: any) => {
            const actions = ['loadInfo'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigateEcommerceMerchant$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => ['payment-methods'].includes(routeData.data.pageId)),
        switchMap((response: any) => {
            const actions = ['loadEcommercePaymentMethods'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigateEcommerceEmailsList$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => ['emails'].includes(routeData.data.pageId)),
        switchMap((response: any) => {
            const actions = ['loadEcommerceEmailsList', 'loadEcommerceEmailsSettings'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigateEcommerceTaxSettings$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => ['tax'].includes(routeData.data.pageId)),
        switchMap((response: any) => {
            const actions = ['loadEcommerceTaxSettings', 'loadEcommerceGeneralSettings', 'loadEcommerceTaxRates', 'loadEcommerceTaxClasses'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigateEcommerceRefundsInfo$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => ['order-view'].includes(routeData.data.pageId)),
        switchMap((response: any) => {
            const orderId = response?.url.match(/\d+/)[0];
            const otherData = {orderId: orderId};
            const actions = ['loadEcommerceRefunds'];
            return this.getActions(actions, response.queryParams.id, otherData);
        })
    ));

    routerNavigateEcommerceAnalytics$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => ['overview'].includes(routeData.data.pageId)),
        switchMap((response: any) => {
            const actions = ['loadEcommerceAnalytics'];

            const today = new Date();

            const sevenDaysBefore = new Date(today);
            sevenDaysBefore.setDate(today.getDate() - 6);

            const formatedAfterDateNow = Utils.formatDateYearMonthDay(sevenDaysBefore);
            const formatedBeforeDateNow = Utils.formatDateYearMonthDay(today);

            const afterDateNow = formatedAfterDateNow + 'T00:00:00';
            const beforeDateNow = formatedBeforeDateNow + 'T23:59:59';

            const fourteenDaysBefore = new Date(today);
            fourteenDaysBefore.setDate(today.getDate() - 7);

            const twentyOneDaysBefore = new Date(today);
            twentyOneDaysBefore.setDate(today.getDate() - 13);

            const formatedAfterDateLastWeek = Utils.formatDateYearMonthDay(twentyOneDaysBefore);
            const formatedBeforeDateLastWeek = Utils.formatDateYearMonthDay(fourteenDaysBefore);

            const afterDateLastWeek = formatedAfterDateLastWeek + 'T00:00:00';
            const beforeDateLastWeek = formatedBeforeDateLastWeek + 'T23:59:59';

            const otherDataLastWeek = {
                afterDateCurrent: afterDateNow,
                beforeDateCurrent: beforeDateNow,
                afterDatePrevious: afterDateLastWeek,
                beforeDatePrevious: beforeDateLastWeek,
            };

            // Call getActions twice with potentially different parameters or the same, as needed
            return this.getActions(actions, response.queryParams.id, otherDataLastWeek);
        })
    ));


    routerNavigateEcommerceSetupList$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => ['payment-methods'].includes(routeData.data.pageId)),
        switchMap((response: any) => {
            const actions = ['loadEcommerceSetupList'];
            const websiteId = response.url.match(/\d+/) && response.url.match(/\d+/)[0] && parseInt(response.url.match(/\d+/)[0], 10);
            const _websiteIdFromQuery = response?.queryParams?.id && +response?.queryParams?.id;
            return this.getActions(actions, websiteId || _websiteIdFromQuery);
        })
    ));


    routerNavigatedHostingTools$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'hosting-tools'),
        switchMap((response: any) => {
            return this._store.select(fromStore.getWebsitesLoaded)
                .pipe(
                    filter((response: any) => response),
                    switchMap(() => this._store.select(fromStore.getWebsite(response.queryParams.id))),
                    filter((response: any) => {
                        if (response && response.isNetwork && !response.parentId && response.children && response.children.length) {
                            this._childWebsiteId = response.children[0];
                        }
                        return !!(response);
                    }),
                    map(() => response)
                );
        }),
        switchMap((response: any) => {
            const actions = ['loadInfo', 'loadZones', 'loadCertificates', 'loadCacheExclude', 'loadHostingSettings', 'loadPhpConfigs'];
            return this.getActions(actions, this._childWebsiteId || response.queryParams.id);
        })
    ));

    routerNavigatedBuilderTemplates$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'builder' || routeData.data.pageId === 'ai-builder' || routeData.data.pageId === 'recreate-website' || routeData.data.pageId === 'builder-trial' || routeData.data.pageId === 'onboarding' || routeData.data.pageId === 'onboarding-templates' || routeData.data.pageId === 'onboarding-recreate-website'),
        switchMap((response: any) => {
            return this._store.select(fromStore.getWebsitePluginsLoaded(response.queryParams.id))
                .pipe(
                    filter((response: boolean) => !!response),
                    switchMap(() => this._store.select(fromStore.getWebsiteBuilderPlugin(response.queryParams.id))),
                    filter((response: Product) => response && response.installed && response.active),
                    map(() => response)
                );
        }),
        switchMap((response: any) => {
            const actions = ['LoadBuilderTemplates', 'LoadLastBuilderTemplate', 'LoadBuilderTemplatesCategories'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigatedAiBuilder$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'ai-builder'),
        switchMap((response: any) => {
            const actions = ['loadGeneratedWebsiteCount'];
            return this.getActions(actions);
        })
    ));

    routerNavigatedPlugins$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'plugins' || routeData.data.pageId === 'themes' || routeData.data.pageId === 'image-optimizer' || routeData.data.pageId === 'seo-overview' || routeData.data.pageId === 'builder' || routeData.data.pageId === 'templates' || routeData.data.pageId === 'recreate-website' || routeData.data.pageId === 'onboarding-recreate-website'),
        switchMap((response: any) => {
            const actions = ['loadWebsiteProducts'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigatedRedirects$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'redirects'),
        switchMap((response: any) => {
            return this._store.select(fromStore.getWebsitesLoaded)
                .pipe(
                    filter((response: any) => response),
                    switchMap(() => this._store.select(fromStore.getWebsite(response.queryParams.id))),
                    filter((response: any) => {
                        if (response && response.isNetwork && !response.parentId && response.children && response.children.length) {
                            this._childWebsiteId = response.children[0];
                        }
                        return !!(response);
                    }),
                    map(() => response)
                );
        }),
        switchMap((response: any) => {
            const actions = ['loadRedirects'];
            return this.getActions(actions, this._childWebsiteId || response.queryParams.id);
        })
    ));

    /*   routerNavigatedSecurity$ = this._actions$.pipe(
        ofType(ROUTER_NAVIGATION), // use the pipeable ofType operator
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'security'),
        switchMap((response: any) => {
          const actions = [
            "loadWebsiteProducts",
            "loadSecurityScan",
            "loadSecurityIgnoredIssues",
            "loadSecuritySettings",
            "loadSecurityAutoupdate",
            "loadSecuritySchedule"
          ];
          return this.getActions(actions, response.queryParams.id);
        }));*/
    routerNavigatedSecurity$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION), // use the pipeable ofType operator
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'security'),
        switchMap((response: any) => {
            return this._store.select(fromStore.getWebsitesLoaded)
                .pipe(
                    filter((response: any) => response),
                    switchMap(() => this._store.select(fromStore.getWebsite(response.queryParams.id))),
                    filter((response: any) => {
                        if (response && response.isNetwork && !response.parentId && response.children && response.children.length) {
                            this._childWebsiteId = response.children[0];
                        }
                        return !!(response);
                    }),
                    map(() => response)
                );
        }),
        switchMap((routeData: any) => {
            const actions = [
                "loadWebsiteProducts",
                "loadSecurityScan",
                "loadSecurityIgnoredIssues",
                "loadSecuritySettings",
                "loadSecurityAutoupdate",
                "loadSecuritySchedule"
            ];
            return this.getActions(actions, this._childWebsiteId || routeData.queryParams.id);
        })));


    /* routerNavigatedLogs$ = this._actions$.ofType(ROUTER_NAVIGATION)
      .pipe(
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'logs'),
        switchMap((response: any) => {
          const actions = ['loadLogs'];
          return this.getActions(actions, response.queryParams.id);
        })
      );*/


    routerNavigatedResourceUsage$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'hosting-resource-usage'),
        switchMap((response: any) => {
            const actions = ['loadHostingVisits', 'loadHostingResourcesByWebsite', 'loadHostingVisitsByWebsiteByDate', 'loadHostingResources'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigatedWebsites$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'websites'),
        switchMap(() => combineLatest(
            this._store.select(fromUserSelectors.getUserSubscription),
            this._store.select(fromUserSelectors.getUser)
        )),
        filter(([userSubscription, user]) => !!userSubscription && !!user),
        switchMap(([userSubscription, user]) => {
            const actions = [];
            actions.push('loadWebsiteTags');
            userSubscription?.plan?.vibeService && actions.push('loadVibeLimitations');
            return this.getActions(actions);
        })
    ));

    routerNavigatedSubscriptionPlan$ = createEffect(() =>
        this._actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            map((event: any) =>
                this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)
            ),
            filter(
                (routeData: RouteData) =>
                    ["/workspace/subscription-plan"].includes(routeData.url)
            ),
            switchMap(() =>
                this._store.select(fromStore.getUserSubscription).pipe(
                    first((userSubscription) => !!userSubscription),
                    switchMap((userSubscription) => {
                        let actions: string[] = [];
                        if (userSubscription?.plan?.vibeService) {
                            actions.push('loadVibeLimitations');
                        }
                        if (!actions.length) {
                            actions = ['empty'];
                        }
                        return this.getActions(actions);
                    })
                )
            )
        )
    );

    routerNavigatedUserPaymentMethods$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData && routeData.data && routeData.url
            && (routeData.data.pageId === 'domains' || ["/workspace/subscription-plan", "/workspace/payment-methods", "/workspace/invoices", "/upgrade-plan", "/agency-special-offer", "/templates", "/search"].includes(routeData.url))),
        switchMap((response: any) => {
            return this._store.select(fromStore.getUserLoaded)
                .pipe(
                    filter((loaded: boolean) => loaded),
                    switchMap(() => combineLatest(
                        this._store.select(fromStore.getUser),
                        this._store.select(fromStore.getCurrentWorkspace),
                    )),
                    filter(([user, _]) => !!user),
                    first()
                );
        }),
        switchMap((response: any) => {
            let actions = ['loadUserPaymentMethods', 'loadUserPayments'];
            response[1] && ![clientRoles.SUITE_DEV].includes(response[1]?.roleId) && (actions = ['empty']);
            return this.getActions(actions);
        })
    ));

    routerNavigatedUserManagement$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => ['user-management', 'activity-log'].includes(routeData.data.pageId)),
        switchMap((response: any) => {
            const actions = ['loadWorkspaceClients'];
            return this.getActions(actions);
        })
    ));
    routerNavigatedWorkspacePages$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'workspace'),
        switchMap((response: any) => {
            const actions = ['loadBillingInfo'];
            return this.getActions(actions);
        })
    ));

    routerNavigatedBoosterPage$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => ['hosting-performance:', 'hosting-performance'].includes(routeData.data.pageId)),
        switchMap((response: any) => {
            const actions = ['loadBenchmarkData'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigatedBoosterMode$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'frontend'),
        switchMap((response: any) => {
            const actions = ['loadBoosterMode'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigatedBoosterAdvancedSettings$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => TypeUtils.isDefined(routeData.data.boosterPage) && !!routeData.data.boosterPage && routeData.data.pageId === 'settings'),
        switchMap((response: any) => {
            const actions = ['loadBoosterAdvancedSettings'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    routerNavigatedBoosterData$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => TypeUtils.isDefined(routeData.data.boosterPage) && !!routeData.data.boosterPage && routeData.data.pageId !== 'cloudflare'),
        switchMap((response: any) => {
            return this._store.select(fromStore.getWebsitesLoaded)
                .pipe(
                    filter((response: any) => response),
                    switchMap(() => this._store.select(fromStore.getWebsite(response.queryParams.id))),
                    filter((response: any) => {
                        if (response && response.site_url) {
                            this._websiteUrl = response.site_url;
                        }
                        return !!(response);
                    }),
                    map(() => response)
                );
        }),
        switchMap((response: any) => {
            const actions = ['loadBoosterData', 'loadBoosterOptimizeStatus', 'loadTroubleShootData', 'loadImageOptimizer'];
            return this.getActions(actions, response.queryParams.id, this._websiteUrl);
        })
    ));

    routerNavigatedImageOptimizerAutoOptimize$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => TypeUtils.isDefined(routeData.data.boosterPage) && !!routeData.data.boosterPage && (routeData.data.pageId === 'booster-image-optimizer' || routeData.data.pageId === 'image-optimizer')),
        switchMap((response: any) => {
            return this._store.select(fromStore.getWebsitesLoaded)
                .pipe(
                    filter((response: any) => response),
                    switchMap(() => this._store.select(fromStore.getWebsite(response.queryParams.id))),
                    filter((response: any) => {
                        if (response && response.site_url) {
                            this._websiteUrl = response.site_url;
                        }
                        return !!(response);
                    }),
                    map(() => response)
                );
        }),
        switchMap((response: any) => {
            const actions = ['loadImageOptimizerAutoOptimize'];
            return this.getActions(actions, response.queryParams.id, this._websiteUrl);
        })
    ));

    routerNavigatedBoosterSoPages$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'frontend'),
        switchMap((response: any) => {
            return this._store.select(fromStore.getUserLoaded)
                .pipe(
                    filter((response: any) => response),
                    switchMap(() => this._store.select(fromStore.getUser)),
                    distinctUntilChanged((userOld: User, userNew: User) => userOld && userNew && userOld.id === userNew.id),
                    filter((response: any) => {
                        if (response) {
                            this._user = response;
                        }
                        return !!(response);
                    }),
                    map(() => response)
                );
        }),
        switchMap((response: any) => {
            const actions = ['loadSoPages'];
            return this.getActions(this._user.starterClient || this._user.oneProductOrBundleClient ? actions : ['empty'], response.queryParams.id);
        })
    ));


    routerNavigatedBoosterCustomSoPages$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => routeData.data.pageId === 'frontend'),
        switchMap((response: any) => {
            return this._store.select(fromStore.getUserLoaded)
                .pipe(
                    filter((response: any) => response),
                    switchMap(() => this._store.select(fromStore.getUser)),
                    distinctUntilChanged((userOld: User, userNew: User) => userOld && userNew && userOld.id === userNew.id),
                    filter((response: any) => {
                        if (response) {
                            this._user = response;
                        }
                        return !!(response);
                    }),
                    map(() => response)
                );
        }),
        switchMap((response: any) => {
            const actions = ['loadCustomSoPages'];
            return this.getActions(!this._user.starterClient && !this._user.oneProductOrBundleClient ? actions : ['empty'], response.queryParams.id);
        })
    ));

    routerNavigatedCDN$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => TypeUtils.isDefined(routeData.data.boosterPage) && !!routeData.data.boosterPage),
        switchMap((response: any) => {
            return this._store.select(fromStore.getWebsitesLoaded)
                .pipe(
                    filter((response: any) => response),
                    switchMap(() => this._store.select(fromStore.getWebsite(response.queryParams.id))),
                    filter((response: any) => {
                        if (response && response.id) {
                            this._website = response;
                        } else {
                            this._website = null;
                        }
                        return !!(response);
                    }),
                    map(() => response)
                );
        }),
        switchMap((response: any) => {
            let actions = [];
            /*      if (!Utils.checkStringInArray(this._website.site_url, Config.hostnameList)) {
                    actions.push('loadCloudflareDefaultRecord');
                  }*/
            if (!!this._website.cloudflareEnabled && actions) {
                actions = [...actions, ...['loadCloudflareData', 'loadCacheRules', 'loadWebsiteBandwidth']];
            }
            !actions.length && (actions = ['empty']);
            return this.getActions(actions, response.queryParams.id);
        })
    ));


    routerNavigated10webApi$ = createEffect(() => this._actions$.pipe(
        ofType(ROUTER_NAVIGATION),
        map((event: any) => this._routeDataService.getRouteDataByRoute(event.payload.routerState.root)),
        filter((routeData: RouteData) => ['api-key', 'webhook', 'pre-installed-plugins'].includes(routeData.data.pageId)),
        switchMap((response: any) => {
            const actions = ['loadResellerClientData'];
            return this.getActions(actions, response.queryParams.id);
        })
    ));

    private getActions(actions: string[], websiteId?: number, otherData?): Action[] {
        return actions.reduce((_actions: any, action: any): Action => {
            _actions.push(this.getResource(action, websiteId, otherData));
            return _actions;
        }, []);

    }

    private getResource(resourceId: string, websiteId?: number, otherData?): Action {
        const resourceFullId: string = websiteId ? resourceId + 'x' + websiteId : resourceId;
        const domainIds = otherData && otherData.domainIds ? otherData.domainIds : null;
        const expanded = otherData && otherData.expanded ? otherData.expanded : null;
        const datePicker = otherData && otherData.datePicker ? otherData.datePicker : null;
        if (!this._loadedResources.includes(resourceFullId)) {
            switch (resourceId) {
                case 'loadWebsiteProducts':
                    this._loadedResources.push(resourceFullId);
                    return new websiteProductActions.LoadWebsiteProducts(websiteId);

                case 'loadWebsiteWPVersion':
                    this._loadedResources.push(resourceFullId);
                    return new websiteWpVersionActions.LoadWordpressVersion(websiteId);

                case 'loadSecurityScan':
                    this._loadedResources.push(resourceFullId);
                    return new securityAction.LoadSecurityScan(websiteId);

                case 'loadSecurityIgnoredIssues':
                    this._loadedResources.push(resourceFullId);
                    return new securityAction.LoadSecurityIgnoredIssues(websiteId);

                case 'loadSecuritySettings':
                    this._loadedResources.push(resourceFullId);
                    return new securityAction.LoadSecuritySettings(websiteId);

                case 'loadSecurityAutoupdate':
                    this._loadedResources.push(resourceFullId);
                    return new securityAction.LoadSecurityAutoupdate(websiteId);

                case 'loadSecuritySchedule':
                    this._loadedResources.push(resourceFullId);
                    return new securityAction.LoadSecuritySchedule(websiteId);
                case 'loadPerformancesHistory':
                    this._loadedResources.push(resourceFullId);
                    return new performanceAction.LoadPerformancesHistory(websiteId);

                case 'loadHostingResourcesByWebsite':
                    this._loadedResources.push(resourceFullId);
                    return new HostingResourcesAction.LoadHostingResourcesByWebsite(websiteId);

                case 'loadHostingResources':
                    this._loadedResources.push(resourceFullId);
                    return new HostingResourcesAction.LoadHostingResources(websiteId);

                case 'loadHostingVisits':
                    this._loadedResources.push(resourceFullId);
                    return new HostingVisitsAction.LoadHostingVisits(websiteId);

                case 'loadHostingVisitsByWebsiteByDate':
                    this._loadedResources.push(resourceFullId);
                    return new HostingVisitsAction.LoadHostingVisitsByWebsiteByDate({websiteId: websiteId, date: null});

                case 'performancesHistory':
                    this._loadedResources.push(resourceFullId);
                    return new performanceAction.LoadPerformancesHistory(websiteId);

                case 'performancesTest':
                    this._loadedResources.push(resourceFullId);
                    return new performanceAction.LoadPerformanceTest({
                        websiteId: websiteId,
                        performanceTestId: otherData
                    });

                case 'loadDomains':
                    this._loadedResources.push(resourceFullId);
                    return new hostingDomainsActions.LoadDomains({
                        websiteId: websiteId,
                        childWebsiteId: this._childWebsiteId
                    });

                case 'loadInfo':
                    this._loadedResources.push(resourceFullId);
                    return new hostingInfoActions.LoadInfo(websiteId);

                case 'loadZones':
                    this._loadedResources.push(resourceFullId);
                    return new hostingDomainsActions.LoadZones();

                case 'loadCertificates':
                    this._loadedResources.push(resourceFullId);
                    return new hostingCertificatesAction.LoadCertificates(websiteId);

                case 'loadVibeLimitations':
                    this._loadedResources.push(resourceFullId);
                    return new vibeLimitationsActions.LoadVibeLimitations();

                case 'loadCacheExclude':
                    this._loadedResources.push(resourceFullId);
                    return new hostingCacheExcludeAction.LoadCacheExclude(websiteId);

                case 'loadHostingSettings':
                    this._loadedResources.push(resourceFullId);
                    return new hostingSettingsAction.LoadHostingSettings(websiteId);

                case 'loadPhpConfigs':
                    this._loadedResources.push(resourceFullId);
                    return new hostingPhpConfigsAction.LoadPhpConfigs(websiteId);

                case 'loadRedirects':
                    this._loadedResources.push(resourceFullId);
                    return new hostingRedirectsActions.LoadRedirects(websiteId);

                case 'loadLogs':
                    return new hostingLogsActions.LoadLogs(websiteId, 1000, "access");

                case 'LoadBuilderTemplates':
                    this._loadedResources.push(resourceFullId);
                    return new builderTemplatesAction.LoadBuilderTemplates();

                case 'LoadLastBuilderTemplate':
                    this._loadedResources.push(resourceFullId);
                    return new builderTemplatesAction.LoadLastBuilderTemplate({
                        websiteId: websiteId,
                        lastEntities: null
                    });

                case 'LoadBuilderTemplatesCategories':
                    this._loadedResources.push(resourceFullId);
                    return new builderTemplatesAction.LoadBuilderTemplatesCategories();

                case 'loadWebsiteTags':
                    this._loadedResources.push(resourceFullId);
                    return new websiteTagsAction.LoadWebsiteTags();

                case 'loadUserPaymentMethods':
                    this._loadedResources.push(resourceFullId);
                    return new userPaymentMethodsAction.LoadUserPaymentMethods();

                case 'loadUserPayments':
                    this._loadedResources.push(resourceFullId);
                    return new userPaymentsAction.LoadUserPayments();

                case 'loadWorkspaceClients':
                    this._loadedResources.push(resourceFullId);
                    return new workspaceActions.LoadWorkspaceAllClients();

                case 'loadBillingInfo':
                    return new workspaceActions.LoadBillingInfo();

                case 'loadBuilderCreatedPages':
                    this._loadedResources.push(resourceFullId);
                    return new aiBuilderActions.LoadBuilderCreatedPages(websiteId);

                case 'loadBenchmarkData':
                    this._loadedResources.push(resourceFullId);
                    return new boosterActions.LoadBoosterBenchmark({websiteId});

                case 'loadBoosterData':
                    this._loadedResources.push(resourceFullId);
                    return new boosterDataActions.LoadBoosterData({websiteId: websiteId});

                case 'loadBoosterAdvancedSettings':
                    this._loadedResources.push(resourceFullId);
                    return new boosterAdvancedSettings.LoadBoosterAdvancedSettings(websiteId);

                case 'loadImageOptimizer':
                    this._loadedResources.push(resourceFullId);
                    return new imageOptimizerActions.LoadImageOptimizer({websiteId: websiteId});

                case 'loadImageOptimizerAutoOptimize':
                    this._loadedResources.push(resourceFullId);
                    return new imageOptimizerActions.LoadImageOptimizerAutoOptimize({websiteId: websiteId});

                case 'loadTroubleShootData':
                    this._loadedResources.push(resourceFullId);
                    return new troubleShootDataActions.LoadTroubleShootData({
                        websiteId: websiteId,
                        websiteUrl: otherData
                    });

                case 'loadBoosterOptimizeStatus':
                    this._loadedResources.push(resourceFullId);
                    return new boosterDataActions.LoadBoosterOptimizeStatus({websiteId: websiteId, pageUrl: otherData});

                case 'loadCloudflareData':
                    this._loadedResources.push(resourceFullId);
                    return new cloudflareDataActions.LoadCloudflareData(websiteId);

                case 'loadCacheRules':
                    this._loadedResources.push(resourceFullId);
                    return new cacheRulesActions.LoadCacheRules(websiteId);

                case 'loadEcommerceActiveMerchant':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceMerchant.LoadEcommerceActiveMerchant({websiteId});

                case 'loadEcommerceEmailsList':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceEmailsList.LoadEcommerceEmailsList({websiteId});

                case 'loadEcommerceTaxSettings':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceTaxSettings.LoadEcommerceTaxSettings({websiteId});

                case 'loadEcommerceTaxClasses':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceTaxClasses.LoadEcommerceTaxClasses({websiteId});

                case 'loadEcommerceTaxRates':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceTaxRates.LoadEcommerceTaxRates({websiteId});

                case 'loadEcommerceGeneralSettings':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceGeneralSettings.LoadEcommerceGeneralSettings({websiteId});

                case 'loadEcommerceEmailsSettings':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceEmailsSettings.LoadEcommerceEmailsSettings({websiteId});

                case 'loadEcommerceMerchantHash':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceMerchant.LoadEcommerceMerchantHash({
                        websiteId,
                        merchantId: otherData?.stripe_account_id,
                        env: otherData?.env
                    });

                case 'loadEcommercePaymentMethods':
                    this._loadedResources.push(resourceFullId);
                    return new ecommercePaymentMethods.LoadEcommercePaymentMethods({websiteId});

                case 'loadEcommercePublicKey':
                    this._loadedResources.push(resourceFullId);
                    return new ecommercePublicKey.LoadEcommercePublicKey({websiteId});

                case 'loadEcommercePerformanceNow':
                    this._loadedResources.push(resourceFullId);
                    return new ecommercePerformanceNow.LoadEcommercePerformanceNow({
                        websiteId,
                        datePicker: '&after=' + otherData?.afterDate + '&before=' + otherData?.beforeDate
                    });

                case 'loadEcommerceAnalytics':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceAnalytics.LoadEcommerceAnalytics({
                        websiteId,
                        datePicker: 'after_current=' + otherData?.afterDateCurrent + '&before_current=' + otherData?.beforeDateCurrent + '&after_previous=' + otherData?.afterDatePrevious + '&before_previous=' + otherData?.beforeDatePrevious
                    });

                case 'loadEcommerceRefunds':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceRefunds.LoadEcommerceRefunds({websiteId, orderId: otherData?.orderId});

                case 'loadEcommercePerformanceLastYear':
                    this._loadedResources.push(resourceFullId);
                    return new ecommercePerformance.LoadEcommercePerformance({
                        websiteId,
                        datePicker: '&after=' + otherData?.afterDate + '&before=' + otherData?.beforeDate
                    });

                case 'loadEcommercePerformanceReportOrders':
                    this._loadedResources.push(resourceFullId);
                    return new ecommercePerformanceReportOrders.LoadEcommercePerformanceReportOrders({
                        websiteId,
                        datePicker: '&after=' + otherData?.afterDate + '&before=' + otherData?.beforeDate
                    });

                case 'loadEcommercePerformanceReportOrdersLastYear':
                    this._loadedResources.push(resourceFullId);
                    return new ecommercePerformanceReportOrdersLastYear.LoadEcommercePerformanceReportOrdersLastYear({
                        websiteId,
                        datePicker: '&after=' + otherData?.afterDate + '&before=' + otherData?.beforeDate
                    });

                case 'loadEcommercePerformanceReportNetSales':
                    this._loadedResources.push(resourceFullId);
                    return new ecommercePerformanceReportNetSales.LoadEcommercePerformanceReportNetSales({
                        websiteId,
                        datePicker: '&after=' + otherData?.afterDate + '&before=' + otherData?.beforeDate
                    });

                case 'loadEcommercePerformanceReportNetSalesLastYear':
                    this._loadedResources.push(resourceFullId);
                    return new ecommercePerformanceReportNetSalesLastYear.LoadEcommercePerformanceReportNetSalesLastYear({
                        websiteId,
                        datePicker: '&after=' + otherData?.afterDate + '&before=' + otherData?.beforeDate
                    });

                case 'loadEcommerceWorkspaceMerchants':
                    this._loadedResources.push(resourceFullId);
                    return new ecommerceMerchant.LoadEcommerceWorkspaceMerchants();

                case 'loadEcommerceSetupList':
                    this._loadedResources.push(resourceFullId);
                    return new EcommerceSetupListActions.LoadEcommerceSetupList({websiteId});

                case 'loadWebsiteBandwidth':
                    this._loadedResources.push(resourceFullId);
                    return new bandwidthActions.LoadWebsiteBandwidth(websiteId);

                case 'loadGeneratedWebsiteCount':
                    this._loadedResources.push(resourceFullId);
                    return new aiBuilderActions.LoadGeneratedWebsiteCount();

                case 'loadExpandedDomainData':
                    return new expandedDomainDataActions.LoadExpandedDomainsData(websiteId, domainIds, expanded);

                case 'loadResellerClientData':
                    return new tenWebApiActions.GetResellerClientData();

                case 'loadResellerMainDomain':
                  return new tenWebApiActions.GetResellerMainDomain();

                default:
                    return new Foo();

            }

        } else {
            return new Foo();
        }
    }
}
